import { useRef, useState, useEffect } from 'react'
import { Label, Button, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Checkbox } from '@creanizer/ui'
import { Upload, FileText, Users, X, Eye, ChevronUp, ChevronDown, Search, Globe, Shield, Package, Map } from 'lucide-react'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useEnemyStore } from '../../../../stores/enemyStore'
import { useItemStore } from '../../../../stores/itemStore'
import { useDungeonStore } from '../../../../stores/dungeonStore'
import { useCommunityStore } from '../../../../stores/communityStore'
import { useSocietyStore } from '../../../../stores/societyStore'
import type { SavedCharacter, SavedWorldBuilding, SavedEnemy, SavedItem, SavedDungeon, SavedCommunity, SavedSociety } from '@creanizer/types'

interface IdeaModeFormProps {
  ideaText: string
  setIdeaText: (text: string) => void
  savedCommunities?: SavedCommunity[]
  savedSocieties?: SavedSociety[]
}

export function IdeaModeForm({ ideaText, setIdeaText, savedCommunities: propsSavedCommunities, savedSocieties: propsSavedSocieties }: IdeaModeFormProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { characters } = useCharacterStore()
  const { savedWorlds } = useWorldBuilderStore()
  const { enemies } = useEnemyStore()
  const { items } = useItemStore()
  const { dungeons } = useDungeonStore()
  const communityStore = useCommunityStore()
  const societyStore = useSocietyStore()
  
  // Props from parent or fallback to store
  const savedCommunities = propsSavedCommunities || communityStore.savedCommunities
  const savedSocieties = propsSavedSocieties || societyStore.savedSocieties
  const [selectedCharacters, setSelectedCharacters] = useState<string[]>([])
  const [selectedEnemies, setSelectedEnemies] = useState<string[]>([])
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [selectedCommunities, setSelectedCommunities] = useState<string[]>([])
  const [selectedSocieties, setSelectedSocieties] = useState<string[]>([])
  const [selectedWorldId, setSelectedWorldId] = useState<string | null>(null)
  const [selectedDungeonId, setSelectedDungeonId] = useState<string | null>(null)
  const [userText, setUserText] = useState<string>('') // ユーザー入力用の別ステート
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ id: string; name: string; content: string }>>([]) // 読み込んだファイルのリスト
  const [viewingCharacter, setViewingCharacter] = useState<SavedCharacter | null>(null) // 詳細表示中のキャラクター
  const [viewingEnemy, setViewingEnemy] = useState<SavedEnemy | null>(null) // 詳細表示中のエネミー
  const [viewingItem, setViewingItem] = useState<SavedItem | null>(null) // 詳細表示中のアイテム
  const [viewingCommunity, setViewingCommunity] = useState<SavedCommunity | null>(null) // 詳細表示中のコミュニティ
  const [viewingSociety, setViewingSociety] = useState<SavedSociety | null>(null) // 詳細表示中のソサエティ
  const [searchQuery, setSearchQuery] = useState<string>('') // 検索クエリ
  const [enemySearchQuery, setEnemySearchQuery] = useState<string>('') // エネミー検索クエリ
  const [itemSearchQuery, setItemSearchQuery] = useState<string>('') // アイテム検索クエリ
  const [communitySearchQuery, setCommunitySearchQuery] = useState<string>('') // コミュニティ検索クエリ
  const [societySearchQuery, setSocietySearchQuery] = useState<string>('') // ソサエティ検索クエリ
  const [showCharacterSelector, setShowCharacterSelector] = useState<boolean>(false) // キャラクター選択エリアの表示/非表示
  const [showEnemySelector, setShowEnemySelector] = useState<boolean>(false) // エネミー選択エリアの表示/非表示
  const [showItemSelector, setShowItemSelector] = useState<boolean>(false) // アイテム選択エリアの表示/非表示
  const [showCommunitySelector, setShowCommunitySelector] = useState<boolean>(false) // コミュニティ選択エリアの表示/非表示
  const [showSocietySelector, setShowSocietySelector] = useState<boolean>(false) // ソサエティ選択エリアの表示/非表示
  
  // コンポーネント初期化時にユーザー入力部分を抽出
  useEffect(() => {
    const textWithoutCharacters = ideaText.split('【保存されたキャラクター】')[0].trim()
    setUserText(textWithoutCharacters)
    // 初期状態で選択されているキャラクターがある場合は開いておく
    if (selectedCharacters.length > 0) {
      setShowCharacterSelector(true)
    }
    if (selectedEnemies.length > 0) {
      setShowEnemySelector(true)
    }
    if (selectedItems.length > 0) {
      setShowItemSelector(true)
    }
    if (selectedCommunities.length > 0) {
      setShowCommunitySelector(true)
    }
    if (selectedSocieties.length > 0) {
      setShowSocietySelector(true)
    }
  }, []) // 初回のみ実行

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // テキストファイルのみ許可
    if (!file.type.startsWith('text/')) {
      alert('テキストファイルのみアップロード可能です')
      return
    }

    // ファイルサイズ制限を削除（Gemini 2.5は大容量対応可能）
    // 必要に応じて極端に大きいファイル（例：100MB以上）のみ警告
    if (file.size > 100 * 1024 * 1024) {
      const confirm = window.confirm('ファイルサイズが100MBを超えています。処理に時間がかかる可能性がありますが、続行しますか？')
      if (!confirm) return
    }

    try {
      const text = await file.text()
      // ファイル情報を追加（IDはタイムスタンプ+ランダム文字列）
      const fileId = `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
      const newFile = { id: fileId, name: file.name, content: text }
      
      setUploadedFiles(prev => [...prev, newFile])
      // updateIdeaTextを修正版で呼び出す
      const newFiles = [...uploadedFiles, newFile]
      updateIdeaTextWithFiles(userText, newFiles, selectedCharacters, selectedEnemies, selectedItems, selectedCommunities, selectedSocieties, selectedWorldId, selectedDungeonId)
    } catch (error) {
      console.error('ファイル読み込みエラー:', error)
      alert('ファイルの読み込みに失敗しました')
    }

    // ファイル選択をリセット
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // ファイルを削除する関数
  const handleFileRemove = (fileId: string) => {
    const newFiles = uploadedFiles.filter(f => f.id !== fileId)
    setUploadedFiles(newFiles)
    updateIdeaTextWithFiles(userText, newFiles, selectedCharacters, selectedEnemies, selectedItems, selectedCommunities, selectedSocieties, selectedWorldId, selectedDungeonId)
  }

  // uploadedFilesを含めた新しいupdateIdeaText関数
  const updateIdeaTextWithFiles = (baseText: string, files: Array<{ id: string; name: string; content: string }>, selectedCharIds: string[], selectedEnemyIds: string[], selectedItemIds: string[], selectedCommunityIds: string[], selectedSocietyIds: string[], worldId: string | null, dungeonId: string | null) => {
    // ユーザーテキストとアップロードファイルを結合
    let combinedText = baseText
    
    // アップロードされたファイルの内容を追加
    if (files.length > 0) {
      const filesContent = files.map(file => {
        return `【ファイル: ${file.name}】\n${file.content}`
      }).join('\n\n')
      combinedText = baseText + (baseText ? '\n\n' : '') + filesContent
    }
    
    // 既存のupdateIdeaTextのロジックを使用
    updateIdeaText(combinedText, selectedCharIds, selectedEnemyIds, selectedItemIds, selectedCommunityIds, selectedSocietyIds, worldId, dungeonId)
  }
  
  // キャラクター・エネミー・アイテム・コミュニティ・ソサエティ情報と世界観・ダンジョンをユーザーテキストに結合する関数
  const updateIdeaText = (baseText: string, selectedCharIds: string[], selectedEnemyIds: string[], selectedItemIds: string[], selectedCommunityIds: string[], selectedSocietyIds: string[], worldId: string | null, dungeonId: string | null) => {
    let combinedText = baseText
    
    // 世界観を追加（全データをJSON形式で含める）
    if (worldId) {
      const selectedWorld = savedWorlds.find(w => w.id === worldId)
      if (selectedWorld) {
        const worldContent = `【世界観：${selectedWorld.result.name}】
${JSON.stringify(selectedWorld.result)}`
        combinedText += `${combinedText ? '\n\n' : ''}${worldContent}`
      }
    }
    
    // ダンジョンを追加（全データをJSON形式で含める）
    if (dungeonId) {
      const selectedDungeon = dungeons.find(d => d.id === dungeonId)
      if (selectedDungeon) {
        const dungeonContent = `【ダンジョン：${selectedDungeon.result.basicInfo.name}】
${JSON.stringify(selectedDungeon.result)}`
        combinedText += `${combinedText ? '\n\n' : ''}${dungeonContent}`
      }
    }
    
    // キャラクターを追加（全データをJSON形式で含める）
    if (selectedCharIds.length > 0) {
      const selectedChars = characters.filter(c => selectedCharIds.includes(c.id))
      const characterContent = selectedChars.map(char => {
        return `【キャラクター：${char.result.name}】
${JSON.stringify(char.result)}`
      }).join('\n\n')
      
      combinedText += `${combinedText ? '\n\n' : ''}【保存されたキャラクター】\n${characterContent}`
    }
    
    // エネミーを追加（全データをJSON形式で含める）
    if (selectedEnemyIds.length > 0) {
      const selectedEns = enemies.filter(e => selectedEnemyIds.includes(e.id))
      const enemyContent = selectedEns.map(enemy => {
        return `【エネミー：${enemy.result.basicInfo.name}】
${JSON.stringify(enemy.result)}`
      }).join('\n\n')
      
      combinedText += `${combinedText ? '\n\n' : ''}【保存されたエネミー】\n${enemyContent}`
    }
    
    // アイテムを追加（全データをJSON形式で含める）
    if (selectedItemIds.length > 0) {
      const selectedItms = items.filter(i => selectedItemIds.includes(i.id))
      const itemContent = selectedItms.map(item => {
        return `【アイテム：${item.result.basicInfo.name}】
${JSON.stringify(item.result)}`
      }).join('\n\n')
      
      combinedText += `${combinedText ? '\n\n' : ''}【保存されたアイテム】\n${itemContent}`
    }
    
    // コミュニティを追加
    if (selectedCommunityIds.length > 0) {
      const communityContent = selectedCommunityIds.map(id => {
        const community = savedCommunities.find(c => c.id === id)
        if (!community) return ''
        return `【コミュニティ：${community.result.basicInfo.name}】
${JSON.stringify(community.result)}`
      }).join('\n\n')
      
      combinedText += `${combinedText ? '\n\n' : ''}【保存されたコミュニティ】\n${communityContent}`
    }
    
    // ソサエティを追加
    if (selectedSocietyIds.length > 0) {
      const societyContent = selectedSocietyIds.map(id => {
        const society = savedSocieties.find(s => s.id === id)
        if (!society) return ''
        return `【組織：${society.result.basicInfo.name}】
${JSON.stringify(society.result)}`
      }).join('\n\n')
      
      combinedText += `${combinedText ? '\n\n' : ''}【保存された組織】\n${societyContent}`
    }
    
    setIdeaText(combinedText)
  }

  // キャラクター選択時の処理
  const handleCharacterToggle = (characterId: string) => {
    const newSelectedCharacters = selectedCharacters.includes(characterId)
      ? selectedCharacters.filter(id => id !== characterId)
      : [...selectedCharacters, characterId]
    
    setSelectedCharacters(newSelectedCharacters)
    updateIdeaTextWithFiles(userText, uploadedFiles, newSelectedCharacters, selectedEnemies, selectedItems, selectedCommunities, selectedSocieties, selectedWorldId, selectedDungeonId)
  }
  
  // エネミー選択時の処理
  const handleEnemyToggle = (enemyId: string) => {
    const newSelectedEnemies = selectedEnemies.includes(enemyId)
      ? selectedEnemies.filter(id => id !== enemyId)
      : [...selectedEnemies, enemyId]
    
    setSelectedEnemies(newSelectedEnemies)
    updateIdeaTextWithFiles(userText, uploadedFiles, selectedCharacters, newSelectedEnemies, selectedItems, selectedCommunities, selectedSocieties, selectedWorldId, selectedDungeonId)
  }
  
  // アイテム選択時の処理
  const handleItemToggle = (itemId: string) => {
    const newSelectedItems = selectedItems.includes(itemId)
      ? selectedItems.filter(id => id !== itemId)
      : [...selectedItems, itemId]
    
    setSelectedItems(newSelectedItems)
    updateIdeaTextWithFiles(userText, uploadedFiles, selectedCharacters, selectedEnemies, newSelectedItems, selectedCommunities, selectedSocieties, selectedWorldId, selectedDungeonId)
  }
  
  // コミュニティ選択時の処理
  const handleCommunityToggle = (communityId: string) => {
    const newSelectedCommunities = selectedCommunities.includes(communityId)
      ? selectedCommunities.filter(id => id !== communityId)
      : [...selectedCommunities, communityId]
    
    setSelectedCommunities(newSelectedCommunities)
    updateIdeaTextWithFiles(userText, uploadedFiles, selectedCharacters, selectedEnemies, selectedItems, newSelectedCommunities, selectedSocieties, selectedWorldId, selectedDungeonId)
  }
  
  // ソサエティ選択時の処理
  const handleSocietyToggle = (societyId: string) => {
    const newSelectedSocieties = selectedSocieties.includes(societyId)
      ? selectedSocieties.filter(id => id !== societyId)
      : [...selectedSocieties, societyId]
    
    setSelectedSocieties(newSelectedSocieties)
    updateIdeaTextWithFiles(userText, uploadedFiles, selectedCharacters, selectedEnemies, selectedItems, selectedCommunities, newSelectedSocieties, selectedWorldId, selectedDungeonId)
  }
  
  // 世界観選択時の処理
  const handleWorldSelect = (worldId: string | null) => {
    setSelectedWorldId(worldId)
    updateIdeaTextWithFiles(userText, uploadedFiles, selectedCharacters, selectedEnemies, selectedItems, selectedCommunities, selectedSocieties, worldId, selectedDungeonId)
  }
  
  // ダンジョン選択時の処理
  const handleDungeonSelect = (dungeonId: string | null) => {
    setSelectedDungeonId(dungeonId)
    updateIdeaTextWithFiles(userText, uploadedFiles, selectedCharacters, selectedEnemies, selectedItems, selectedCommunities, selectedSocieties, selectedWorldId, dungeonId)
  }
  
  // ユーザーテキスト変更時の処理
  const handleUserTextChange = (text: string) => {
    setUserText(text)
    updateIdeaTextWithFiles(text, uploadedFiles, selectedCharacters, selectedEnemies, selectedItems, selectedCommunities, selectedSocieties, selectedWorldId, selectedDungeonId)
  }

  // フィルタリングされたキャラクターリスト
  const filteredCharacters = characters.filter(character => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      character.result.name.toLowerCase().includes(query) ||
      character.result.role.toLowerCase().includes(query) ||
      character.result.summary.toLowerCase().includes(query) ||
      character.result.age.toLowerCase().includes(query) ||
      character.result.gender.toLowerCase().includes(query)
    )
  })
  
  // フィルタリングされたエネミーリスト
  const filteredEnemies = enemies.filter(enemy => {
    if (!enemySearchQuery) return true
    const query = enemySearchQuery.toLowerCase()
    return (
      enemy.result.basicInfo.name.toLowerCase().includes(query) ||
      enemy.result.basicInfo.type.toLowerCase().includes(query) ||
      enemy.result.basicInfo.category.toLowerCase().includes(query) ||
      enemy.result.basicInfo.summary.toLowerCase().includes(query) ||
      enemy.result.basicInfo.threat.toLowerCase().includes(query)
    )
  })
  
  // フィルタリングされたアイテムリスト
  const filteredItems = items.filter(item => {
    if (!itemSearchQuery) return true
    const query = itemSearchQuery.toLowerCase()
    return (
      item.result.basicInfo.name.toLowerCase().includes(query) ||
      item.result.basicInfo.type.toLowerCase().includes(query) ||
      item.result.basicInfo.rarity.toLowerCase().includes(query) ||
      item.result.basicInfo.summary.toLowerCase().includes(query)
    )
  })
  
  // フィルタリングされたコミュニティリスト
  const filteredCommunities = savedCommunities.filter(community => {
    if (!community?.result?.basicInfo) return false
    if (!communitySearchQuery) return true
    const query = communitySearchQuery.toLowerCase()
    return (
      community.result.basicInfo.name.toLowerCase().includes(query) ||
      community.result.basicInfo.type.toLowerCase().includes(query) ||
      (community.result.basicInfo.reputation?.toLowerCase().includes(query) ?? false)
    )
  })
  
  // フィルタリングされたソサエティリスト
  const filteredSocieties = savedSocieties.filter(society => {
    if (!society?.result?.basicInfo) return false
    if (!societySearchQuery) return true
    const query = societySearchQuery.toLowerCase()
    return (
      society.result.basicInfo.name.toLowerCase().includes(query) ||
      society.result.basicInfo.type.toLowerCase().includes(query) ||
      (society.result.basicInfo.summary?.toLowerCase().includes(query) ?? false)
    )
  })

  return (
    <div className="space-y-4">
      {/* 保存された世界観から選択 */}
      {savedWorlds.length > 0 && (
        <div className="space-y-2">
          <Label>世界観を選択</Label>
          <Select
            value={selectedWorldId || 'none'}
            onValueChange={(value) => handleWorldSelect(value === 'none' ? null : value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="世界観を選択...">
                {selectedWorldId ? (
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    {savedWorlds.find(w => w.id === selectedWorldId)?.title}
                  </div>
                ) : (
                  '世界観を選択...'
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                <div className="flex items-center gap-2">
                  選択しない
                </div>
              </SelectItem>
              {savedWorlds.map((world) => (
                <SelectItem key={world.id} value={world.id}>
                  <div className="flex flex-col">
                    <div className="font-medium">{world.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {world.result.summary?.substring(0, 50)}...
                    </div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedWorldId && (
            <div className="p-2 bg-blue-50 rounded text-xs">
              <div className="font-medium text-blue-900 mb-1">選択中の世界観</div>
              <div className="text-blue-700">
                {savedWorlds.find(w => w.id === selectedWorldId)?.result.summary?.substring(0, 100)}...
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* 保存されたダンジョンから選択 */}
      {dungeons.length > 0 && (
        <div className="space-y-2">
          <Label>ダンジョンを選択</Label>
          <Select
            value={selectedDungeonId || 'none'}
            onValueChange={(value) => handleDungeonSelect(value === 'none' ? null : value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="ダンジョンを選択...">
                {selectedDungeonId ? (
                  <div className="flex items-center gap-2">
                    <Map className="h-4 w-4" />
                    {dungeons.find(d => d.id === selectedDungeonId)?.title}
                  </div>
                ) : (
                  '選択しない'
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                <div className="flex items-center gap-2">
                  選択しない
                </div>
              </SelectItem>
              {dungeons.map((dungeon) => (
                <SelectItem key={dungeon.id} value={dungeon.id}>
                  <div className="flex flex-col">
                    <div className="font-medium">{dungeon.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {dungeon.result.basicInfo.description?.substring(0, 50)}...
                    </div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedDungeonId && (
            <div className="p-2 bg-purple-50 rounded text-xs">
              <div className="font-medium text-purple-900 mb-1">選択中のダンジョン</div>
              <div className="text-purple-700">
                {dungeons.find(d => d.id === selectedDungeonId)?.result.basicInfo.description?.substring(0, 100)}...
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* 保存されたキャラクターから選択 */}
      {characters.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>保存されたキャラクターを使用</Label>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setShowCharacterSelector(!showCharacterSelector)}
            >
              {showCharacterSelector ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-1" />
                  閉じる
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-1" />
                  開く ({selectedCharacters.length}/{characters.length}人選択中)
                </>
              )}
            </Button>
          </div>
          
          {showCharacterSelector && (
            <>
              {/* 検索バー */}
              {characters.length > 5 && (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="名前、役割、年齢などで検索..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              )}
              
              {/* キャラクターリスト */}
              <div className="p-3 border rounded-lg space-y-2 max-h-48 overflow-y-auto">
                {filteredCharacters.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-2">
                    該当するキャラクターが見つかりません
                  </p>
                ) : (
                  filteredCharacters.map(character => (
              <div key={character.id} className="flex items-start gap-2">
                <Checkbox
                  id={`char-${character.id}`}
                  checked={selectedCharacters.includes(character.id)}
                  onCheckedChange={() => handleCharacterToggle(character.id)}
                />
                <Label 
                  htmlFor={`char-${character.id}`} 
                  className="flex-1 cursor-pointer text-sm"
                >
                  <div className="font-medium">{character.result.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {character.result.age} / {character.result.role} / {character.result.gender}
                  </div>
                </Label>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setViewingCharacter(character)
                  }}
                  title="詳細を表示"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </div>
                  ))
                )}
              </div>
              
              {selectedCharacters.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="h-4 w-4" />
                  <span>{selectedCharacters.length}人のキャラクターを選択中</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setSelectedCharacters([])
                      updateIdeaTextWithFiles(userText, uploadedFiles, [], selectedEnemies, selectedItems, selectedCommunities, selectedSocieties, selectedWorldId, selectedDungeonId)
                    }}
                  >
                    クリア
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
      
      {/* 保存されたエネミーから選択 */}
      {enemies.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>保存されたエネミーを使用</Label>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setShowEnemySelector(!showEnemySelector)}
            >
              {showEnemySelector ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-1" />
                  閉じる
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-1" />
                  開く ({selectedEnemies.length}/{enemies.length}体選択中)
                </>
              )}
            </Button>
          </div>
          
          {showEnemySelector && (
            <>
              {/* 検索バー */}
              {enemies.length > 5 && (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="名前、種別、カテゴリなどで検索..."
                    value={enemySearchQuery}
                    onChange={(e) => setEnemySearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              )}
              
              {/* エネミーリスト */}
              <div className="p-3 border rounded-lg space-y-2 max-h-48 overflow-y-auto">
                {filteredEnemies.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-2">
                    該当するエネミーが見つかりません
                  </p>
                ) : (
                  filteredEnemies.map(enemy => (
              <div key={enemy.id} className="flex items-start gap-2">
                <Checkbox
                  id={`enemy-${enemy.id}`}
                  checked={selectedEnemies.includes(enemy.id)}
                  onCheckedChange={() => handleEnemyToggle(enemy.id)}
                />
                <Label 
                  htmlFor={`enemy-${enemy.id}`} 
                  className="flex-1 cursor-pointer text-sm"
                >
                  <div className="font-medium">{enemy.result.basicInfo.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {enemy.result.basicInfo.type} / {enemy.result.basicInfo.category} / 脅威度{enemy.result.basicInfo.threat}
                  </div>
                </Label>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setViewingEnemy(enemy)
                  }}
                  title="詳細を表示"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </div>
                  ))
                )}
              </div>
              
              {selectedEnemies.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Shield className="h-4 w-4" />
                  <span>{selectedEnemies.length}体のエネミーを選択中</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setSelectedEnemies([])
                      updateIdeaTextWithFiles(userText, uploadedFiles, selectedCharacters, [], selectedItems, selectedCommunities, selectedSocieties, selectedWorldId, selectedDungeonId)
                    }}
                  >
                    クリア
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
      
      {/* 保存されたアイテムから選択 */}
      {items.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>保存されたアイテムを使用</Label>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setShowItemSelector(!showItemSelector)}
            >
              {showItemSelector ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-1" />
                  閉じる
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-1" />
                  開く ({selectedItems.length}/{items.length}個選択中)
                </>
              )}
            </Button>
          </div>
          
          {showItemSelector && (
            <>
              {/* 検索バー */}
              {items.length > 5 && (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="名前、種類、レアリティなどで検索..."
                    value={itemSearchQuery}
                    onChange={(e) => setItemSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              )}
              
              {/* アイテムリスト */}
              <div className="p-3 border rounded-lg space-y-2 max-h-48 overflow-y-auto">
                {filteredItems.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-2">
                    該当するアイテムが見つかりません
                  </p>
                ) : (
                  filteredItems.map(item => (
              <div key={item.id} className="flex items-start gap-2">
                <Checkbox
                  id={`item-${item.id}`}
                  checked={selectedItems.includes(item.id)}
                  onCheckedChange={() => handleItemToggle(item.id)}
                />
                <Label 
                  htmlFor={`item-${item.id}`} 
                  className="flex-1 cursor-pointer text-sm"
                >
                  <div className="font-medium">{item.result.basicInfo.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {item.result.basicInfo.type} / {item.result.basicInfo.rarity}
                  </div>
                </Label>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setViewingItem(item)
                  }}
                  title="詳細を表示"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </div>
                  ))
                )}
              </div>
              
              {selectedItems.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Package className="h-4 w-4" />
                  <span>{selectedItems.length}個のアイテムを選択中</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setSelectedItems([])
                      updateIdeaTextWithFiles(userText, uploadedFiles, selectedCharacters, selectedEnemies, [], selectedCommunities, selectedSocieties, selectedWorldId, selectedDungeonId)
                    }}
                  >
                    クリア
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* 保存されたコミュニティを選択 */}
      {savedCommunities.length > 0 && (
        <div className="space-y-2">
          <Button
            type="button"
            variant="outline"
            className="w-full justify-between"
            onClick={() => setShowCommunitySelector(!showCommunitySelector)}
          >
            <div className="flex items-center gap-2">
              <Map className="h-4 w-4" />
              <span>保存されたコミュニティを使用</span>
              {selectedCommunities.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  ({selectedCommunities.length}個選択中)
                </span>
              )}
            </div>
            {showCommunitySelector ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
          
          {showCommunitySelector && (
            <>
              <div className="space-y-1">
                <Input
                  placeholder="コミュニティを検索..."
                  value={communitySearchQuery}
                  onChange={(e) => setCommunitySearchQuery(e.target.value)}
                  className="h-8"
                />
              </div>
              
              <div className="max-h-60 overflow-y-auto border rounded-md p-2 space-y-2">
                {filteredCommunities.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-4">
                    コミュニティが見つかりません
                  </div>
                ) : (
                  filteredCommunities.map(community => (
                    <div key={community.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                <Checkbox
                  id={`community-${community.id}`}
                  checked={selectedCommunities.includes(community.id)}
                  onCheckedChange={() => handleCommunityToggle(community.id)}
                />
                <Label 
                  htmlFor={`community-${community.id}`} 
                  className="flex-1 cursor-pointer text-sm"
                >
                  <div className="font-medium">{community.result.basicInfo.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {community.result.basicInfo.type} / 人口: {community.result.basicInfo.population?.toLocaleString()}人
                  </div>
                </Label>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setViewingCommunity(community)
                  }}
                  title="詳細を表示"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </div>
                  ))
                )}
              </div>
              
              {selectedCommunities.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Map className="h-4 w-4" />
                  <span>{selectedCommunities.length}個のコミュニティを選択中</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setSelectedCommunities([])
                      updateIdeaTextWithFiles(userText, uploadedFiles, selectedCharacters, selectedEnemies, selectedItems, [], selectedSocieties, selectedWorldId, selectedDungeonId)
                    }}
                  >
                    クリア
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* 保存された組織を選択 */}
      {savedSocieties.length > 0 && (
        <div className="space-y-2">
          <Button
            type="button"
            variant="outline"
            className="w-full justify-between"
            onClick={() => setShowSocietySelector(!showSocietySelector)}
          >
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              <span>保存された組織を使用</span>
              {selectedSocieties.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  ({selectedSocieties.length}個選択中)
                </span>
              )}
            </div>
            {showSocietySelector ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
          
          {showSocietySelector && (
            <>
              <div className="space-y-1">
                <Input
                  placeholder="組織を検索..."
                  value={societySearchQuery}
                  onChange={(e) => setSocietySearchQuery(e.target.value)}
                  className="h-8"
                />
              </div>
              
              <div className="max-h-60 overflow-y-auto border rounded-md p-2 space-y-2">
                {filteredSocieties.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-4">
                    組織が見つかりません
                  </div>
                ) : (
                  filteredSocieties.map(society => (
                    <div key={society.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                <Checkbox
                  id={`society-${society.id}`}
                  checked={selectedSocieties.includes(society.id)}
                  onCheckedChange={() => handleSocietyToggle(society.id)}
                />
                <Label 
                  htmlFor={`society-${society.id}`} 
                  className="flex-1 cursor-pointer text-sm"
                >
                  <div className="font-medium">{society.result.basicInfo.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {society.result.basicInfo.type} / {society.result.basicInfo.scale}
                  </div>
                </Label>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setViewingSociety(society)
                  }}
                  title="詳細を表示"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </div>
                  ))
                )}
              </div>
              
              {selectedSocieties.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Shield className="h-4 w-4" />
                  <span>{selectedSocieties.length}個の組織を選択中</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setSelectedSocieties([])
                      updateIdeaTextWithFiles(userText, uploadedFiles, selectedCharacters, selectedEnemies, selectedItems, selectedCommunities, [], selectedWorldId, selectedDungeonId)
                    }}
                  >
                    クリア
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="idea-text">アイディア・要素・プロット案</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-4 w-4 mr-2" />
            ファイルから読み込み
          </Button>
        </div>
        
        <textarea
          id="idea-text"
          className="w-full min-h-[300px] p-4 border rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={`アイディアや要素を自由に入力してください。

例1（要素の羅列）:
・主人公は記憶喪失の探偵
・舞台は近未来の東京
・AIが人間を超えた世界
・過去の事件が鍵を握る
・裏切り者は最も信頼していた相手

例2（ふんわりとしたプロット）:
記憶を失った主人公が目覚めると、見知らぬ部屋にいた。
窓の外には見たことのない未来都市が広がっている。
ポケットには一枚の写真と謎のメッセージ。
「君だけが真実を知っている」
なぜ自分がここにいるのか、何を知っているのか...

例3（断片的なシーン）:
雨の中での対決シーン
仲間の裏切りが発覚する瞬間
隠された地下施設の発見
最後の選択を迫られる主人公`}
          value={userText}
          onChange={(e) => handleUserTextChange(e.target.value)}
        />
        
        <input
          ref={fileInputRef}
          type="file"
          accept="text/*,.txt,.md"
          className="hidden"
          onChange={handleFileUpload}
        />
        
        {/* 読み込んだファイルのリスト */}
        {uploadedFiles.length > 0 && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
            <div className="text-sm font-medium text-gray-700 mb-2">読み込み済みファイル：</div>
            <div className="space-y-2">
              {uploadedFiles.map((file) => (
                <div key={file.id} className="flex items-center justify-between bg-white p-2 rounded border">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">{file.name}</span>
                    <span className="text-xs text-gray-500">({file.content.length}文字)</span>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => handleFileRemove(file.id)}
                    className="p-1"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <FileText className="h-4 w-4" />
          <span>
            {userText.length > 0 ? `${userText.length}文字入力済み` : '要素やアイディアを入力するか、テキストファイルをアップロードしてください'}
            {uploadedFiles.length > 0 && ` + ファイル${uploadedFiles.length}個（${uploadedFiles.reduce((sum, f) => sum + f.content.length, 0)}文字）`}
          </span>
          {(selectedCharacters.length > 0 || selectedEnemies.length > 0 || selectedItems.length > 0) && (
            <span className="text-blue-600">
              （+ {[
                selectedCharacters.length > 0 && `キャラクター${selectedCharacters.length}人`,
                selectedEnemies.length > 0 && `エネミー${selectedEnemies.length}体`,
                selectedItems.length > 0 && `アイテム${selectedItems.length}個`
              ].filter(Boolean).join('、')}の情報が追加されます）
            </span>
          )}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">💡 アイディアモードのヒント</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>・断片的な要素の羅列でもOK</li>
          <li>・ある程度形になったプロット案でもOK</li>
          <li>・キャラクター設定や世界観の説明でもOK</li>
          <li>・思いついたシーンやセリフでもOK</li>
          <li>・AIがこれらを整理して完成されたプロットに仕上げます</li>
        </ul>
      </div>

      {/* キャラクター詳細表示 */}
      {viewingCharacter && (
        <div className="card mt-4 border-2 border-primary/20">
          <div className="p-6 pb-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{viewingCharacter.result.name}の詳細</h3>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.preventDefault()
                  setViewingCharacter(null)
                }}
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="px-6 pb-6 space-y-3">
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">年齢:</span> {viewingCharacter.result.age}
              </div>
              <div>
                <span className="text-muted-foreground">性別:</span> {viewingCharacter.result.gender}
              </div>
              <div>
                <span className="text-muted-foreground">役割:</span> {viewingCharacter.result.role}
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-1 text-sm">概要</h4>
              <p className="text-sm text-muted-foreground">{viewingCharacter.result.summary}</p>
            </div>

            <div>
              <h4 className="font-semibold mb-1 text-sm">性格</h4>
              <div className="flex flex-wrap gap-1">
                {viewingCharacter.result.personality.traits.map((trait, i) => (
                  <span key={i} className="px-2 py-1 bg-primary/10 rounded text-xs">{trait}</span>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-1 text-sm">背景</h4>
              <p className="text-sm text-muted-foreground">
                {viewingCharacter.result.background.birthplace}出身、{viewingCharacter.result.background.occupation}。
                {viewingCharacter.result.background.family}
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-1 text-sm">外見</h4>
              <p className="text-sm text-muted-foreground">
                {viewingCharacter.result.appearance.height}、{viewingCharacter.result.appearance.build}。
                {viewingCharacter.result.appearance.hairColor}の{viewingCharacter.result.appearance.hairStyle}、
                {viewingCharacter.result.appearance.eyeColor}の瞳。
                {viewingCharacter.result.appearance.overallImpression}
              </p>
            </div>

            {viewingCharacter.result.behavior && viewingCharacter.result.behavior.firstPerson && (
              <div>
                <h4 className="font-semibold mb-1 text-sm">一人称</h4>
                <p className="text-sm font-medium">「{viewingCharacter.result.behavior.firstPerson}」</p>
              </div>
            )}

            <div>
              <h4 className="font-semibold mb-1 text-sm">サンプル台詞</h4>
              <div className="space-y-2">
                {viewingCharacter.result.sampleDialogues.slice(0, 2).map((dialogue, i) => (
                  <div key={i} className="p-2 bg-gray-50 rounded text-sm">
                    <p className="text-xs text-muted-foreground mb-1">{dialogue.situation}</p>
                    <p className="font-medium">「{dialogue.line}」</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              {!selectedCharacters.includes(viewingCharacter.id) && (
                <Button
                  type="button"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault()
                    handleCharacterToggle(viewingCharacter.id)
                    setViewingCharacter(null)
                  }}
                >
                  このキャラクターを追加
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.preventDefault()
                  setViewingCharacter(null)
                }}
              >
                閉じる
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* エネミー詳細表示 */}
      {viewingEnemy && (
        <div className="card mt-4 border-2 border-red-200">
          <div className="p-6 pb-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{viewingEnemy.result.basicInfo.name}の詳細</h3>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.preventDefault()
                  setViewingEnemy(null)
                }}
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="px-6 pb-6 space-y-3">
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">種別:</span> {viewingEnemy.result.basicInfo.type}
              </div>
              <div>
                <span className="text-muted-foreground">カテゴリ:</span> {viewingEnemy.result.basicInfo.category}
              </div>
              <div>
                <span className="text-muted-foreground">脅威度:</span> {viewingEnemy.result.basicInfo.threat}
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-1 text-sm">概要</h4>
              <p className="text-sm text-muted-foreground">{viewingEnemy.result.basicInfo.summary}</p>
            </div>

            <div>
              <h4 className="font-semibold mb-1 text-sm">外見</h4>
              <p className="text-sm text-muted-foreground">{viewingEnemy.result.appearance.overallDescription}</p>
            </div>

            <div>
              <h4 className="font-semibold mb-1 text-sm">生息地</h4>
              <p className="text-sm text-muted-foreground">{viewingEnemy.result.basicInfo.habitat}</p>
            </div>

            {viewingEnemy.result.abilities && viewingEnemy.result.abilities.combatCapabilities.specialAbilities.length > 0 && (
              <div>
                <h4 className="font-semibold mb-1 text-sm">主要能力</h4>
                <p className="text-sm font-medium">{viewingEnemy.result.abilities.combatCapabilities.specialAbilities[0].name}</p>
                <p className="text-xs text-muted-foreground mt-1">{viewingEnemy.result.abilities.combatCapabilities.specialAbilities[0].description}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t">
              {!selectedEnemies.includes(viewingEnemy.id) && (
                <Button
                  type="button"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault()
                    handleEnemyToggle(viewingEnemy.id)
                    setViewingEnemy(null)
                  }}
                >
                  このエネミーを追加
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.preventDefault()
                  setViewingEnemy(null)
                }}
              >
                閉じる
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* アイテム詳細表示 */}
      {viewingItem && (
        <div className="card mt-4 border-2 border-yellow-200">
          <div className="p-6 pb-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{viewingItem.result.basicInfo.name}の詳細</h3>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.preventDefault()
                  setViewingItem(null)
                }}
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="px-6 pb-6 space-y-3">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">種類:</span> {viewingItem.result.basicInfo.type}
              </div>
              <div>
                <span className="text-muted-foreground">レアリティ:</span> {viewingItem.result.basicInfo.rarity}
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-1 text-sm">概要</h4>
              <p className="text-sm text-muted-foreground">{viewingItem.result.basicInfo.summary}</p>
            </div>

            <div>
              <h4 className="font-semibold mb-1 text-sm">外見</h4>
              <p className="text-sm text-muted-foreground">{viewingItem.result.appearance.visualDescription}</p>
            </div>

            {viewingItem.result.effects && viewingItem.result.effects.primaryEffect && (
              <div>
                <h4 className="font-semibold mb-1 text-sm">主要効果</h4>
                <p className="text-sm font-medium">{viewingItem.result.effects.primaryEffect.type}</p>
                <p className="text-xs text-muted-foreground mt-1">{viewingItem.result.effects.primaryEffect.description}</p>
              </div>
            )}

            {viewingItem.result.lore && viewingItem.result.lore.originStory && (
              <div>
                <h4 className="font-semibold mb-1 text-sm">由来</h4>
                <p className="text-sm text-muted-foreground">{viewingItem.result.lore.originStory.narrative}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t">
              {!selectedItems.includes(viewingItem.id) && (
                <Button
                  type="button"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault()
                    handleItemToggle(viewingItem.id)
                    setViewingItem(null)
                  }}
                >
                  このアイテムを追加
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.preventDefault()
                  setViewingItem(null)
                }}
              >
                閉じる
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}